/* eslint-disable no-param-reassign */
import { produce } from 'immer';
import { indexBy, prop, mergeRight } from 'ramda';

import * as types from 'constants/ActionTypes';
import { isErrorLocationChange } from 'modules/app/utils';
import * as campaignFile from 'modules/campaignFiles/actionTypes';
import * as chat from 'modules/chat/actionTypes';

import {
  INTERVENTIONS_FETCH,
  INTERVENTIONS_FETCH_FAILURE,
  INTERVENTIONS_FETCH_SUCCESS,
} from 'modules/interventions/actionTypes';
import { LOCATION_CHANGE } from 'modules/router/actionTypes';

import { indexByProp } from '../utils/global';

/**
 * @typedef {import("modules/customers/types/projections/detailed-customer").DetailedCustomer |
 *    import("modules/customers/types/projections/paginated-customer").PaginatedCustomer
 * } Customer
 * */

/**
 * @typedef {{
 *   data: {
 *      byId: Record<string, Customer>,
 *      all: {
 *        items: Record<string, Customer>,
 *        page: import("modules/global/generic.types").Page,
 *      },
 *      select: {
 *        items: Record<string, Customer>,
 *        links: Record<string, import("modules/global/generic.types").Link>
 *      },
 *   },
 *   apiError: string,
 *   isFetching: boolean,
 *   isExporting: boolean,
 *   currentSearch: {
 *     text: string,
 *   },
 *   ui: {
 *     details: {
 *       history: {
 *         items: Record<string, import('modules/interventions/types/projections/basic-intervention').BasicIntervention>,
 *         page: import("modules/global/generic.types").Page,
 *         links: Record<string, import("modules/global/generic.types").Link>,
 *         loading: boolean,
 *       }
 *     }
 *   },
 *   modal: {
 *     creation: {
 *        visible: boolean,
 *        loading: boolean,
 *     }
 *   },
 *   table: {
 *     pageSize: number,
 *     sort: string,
 *     searchString: string,
 *   },
 * }} CustomerReducer
 */

/** @type {CustomerReducer} */
const initialState = {
  data: {
    byId: {},
    select: { items: {}, links: {} },
    all: {
      items: {},
      page: { size: 10 },
    },
    search: { items: {}, link: {}, page: {} },
  },
  currentSearch: { text: '' },
  apiError: null,
  isFetching: false,
  selectedCustomerId: '', // empty string to handle select + select with form
  ui: {
    details: {
      history: {
        items: {},
        links: {},
        page: {},
        loading: false,
      },
    },
  },
  modal: {
    creation: {
      visible: false,
      loading: false,
    },
  },
  table: {
    pageSize: 10,
    sort: 'modifiedOn,desc',
    searchString: '',
  },
};

const addToByIds = (draft, customers) => {
  customers.forEach(customer => {
    if (customer && customer.id) {
      draft.data.byId[customer.id] = {
        ...(draft.data.byId[customer.id] ?? {}),
        ...customer,
      };
    }
  });
};

/**
 * @param {import("modules/chat/types").Conversation} conversation
 * @returns {Customer[]}
 */
export const extractCustomersFromConversation = ({ customer, latestMessage }) => {
  const customers = [];

  if (customer?.id) {
    customers.push(customer);
  }

  if (latestMessage?.customer?.id && latestMessage.customer.id !== customer?.id) {
    customers.push(latestMessage.customer);
  }
  return customers;
};

const isCustomerInterventions = ({ meta }) => meta?.source === 'customer-interventions';

const reducer = produce(
  /** @param {CustomerReducer} draft  */
  (draft, action) => {
    switch (action.type) {
      case LOCATION_CHANGE: {
        if (isErrorLocationChange(action)) {
          draft.apiError = null;
        }
        break;
      }

      case campaignFile.FETCH_FILTERED_CAMPAIGN_FILES:
      case INTERVENTIONS_FETCH: {
        if (isCustomerInterventions(action)) {
          const shouldReset = action.payload.request.params.page === 0;
          if (shouldReset) {
            draft.ui.details.history.items = {};
          }
          draft.ui.details.history.loading = shouldReset;
          draft.apiError = null;
        }
        break;
      }

      case campaignFile.FETCH_FILTERED_CAMPAIGN_FILES_SUCCESS:
      case INTERVENTIONS_FETCH_SUCCESS: {
        if (isCustomerInterventions(action)) {
          draft.ui.details.history.loading = false;
          draft.ui.details.history.items = mergeRight(
            draft.ui.details.history.items,
            indexByProp('id', action.payload.content)
          );
          draft.ui.details.history.page = action.payload.page;
          draft.ui.details.history.links = indexByProp('rel', action.payload.links);
        }
        break;
      }

      case campaignFile.FETCH_FILTERED_CAMPAIGN_FILES_FAILURE:
      case INTERVENTIONS_FETCH_FAILURE: {
        if (isCustomerInterventions(action)) {
          draft.ui.details.history.loading = false;
          draft.apiError = action.payload;
        }
        break;
      }

      case types.FETCH_CUSTOMERS_SELECT_REQUEST:
      case types.FETCH_CUSTOMER_SELECT_REQUEST:
      case types.UPDATE_CUSTOMER_REQUEST:
        draft.isFetching = true;
        draft.apiError = null;
        break;

      case types.FETCH_CUSTOMERS_REQUEST:
        draft.isFetching = true;
        draft.apiError = null;
        draft.table.pageSize = action.payload.request.params?.size;
        draft.table.sort = action.payload.request.params?.sort;
        draft.table.searchString = action.payload.request.params?.text ?? '';
        break;

      case types.FETCH_CUSTOMER_REQUEST:
        draft.isFetching = true;
        draft.apiError = null;
        break;

      case types.CREATE_CUSTOMER_REQUEST:
        draft.apiError = null;
        draft.modal.creation.loading = true;
        break;

      case types.EXPORT_CUSTOMER_REQUEST:
        draft.isExporting = true;
        draft.apiError = null;
        break;

      case types.EXPORT_CUSTOMER_SUCCESS:
        draft.isExporting = false;
        draft.apiError = null;
        break;

      case types.EXPORT_CUSTOMER_FAILURE:
        draft.isExporting = false;
        draft.apiError = action.payload;
        break;

      case types.FETCH_CUSTOMERS_SUCCESS:
        draft.isFetching = false;
        draft.data.all.items = indexByProp('id', action.payload.content);
        draft.data.all.page = action.payload.page;
        addToByIds(draft, action.payload.content);
        break;

      case types.FETCH_CUSTOMERS_SELECT_SUCCESS: {
        const dictionary = indexByProp('id', action.payload.content);
        draft.isFetching = false;
        draft.data.select.items = mergeRight(draft.data.select.items, dictionary);
        draft.data.select.links = indexByProp('rel', action.payload.links);
        draft.data.all.items = mergeRight(draft.data.all.items, dictionary);
        addToByIds(draft, action.payload.content);
        break;
      }

      case types.FETCH_CUSTOMER_SUCCESS:
        draft.isFetching = false;
        draft.data.all.items = mergeRight(draft.data.all.items, indexBy(prop('id'))([action.payload]));
        addToByIds(draft, [action.payload]);
        break;

      case types.FETCH_CUSTOMER_SELECT_SUCCESS:
        draft.isFetching = false;
        draft.data.select.items = mergeRight(draft.data.select.items, indexByProp('id', [action.payload]));
        addToByIds(draft, [action.payload]);
        break;

      case types.FETCH_CUSTOMERS_FAILURE:
      case types.FETCH_CUSTOMER_FAILURE:
      case types.FETCH_CUSTOMERS_SELECT_FAILURE:
        draft.isFetching = false;
        draft.apiError = action?.payload?.error ?? action?.error;
        break;

      case types.CLEAN_CUSTOMERS_REQUEST:
        draft.data = {
          select: { items: {}, links: {} },
          all: { items: {}, page: {} },
          search: { items: {}, link: {}, page: {} },
        };
        break;

      case types.CLEAN_CUSTOMERS_SELECT:
        draft.data.select = { items: {}, links: {} };
        break;

      case types.DISPLAY_CREATION_MODAL:
        draft.modal.creation.visible = true;
        break;

      case types.HIDE_CREATION_MODAL:
        draft.modal.creation.visible = false;
        break;

      case types.CREATE_CUSTOMER_SUCCESS:
        draft.modal.creation.loading = false;
        draft.modal.creation.visible = false;
        draft.data.all.items = { ...draft.data.all.items, [action.payload.id]: action.payload };
        addToByIds(draft, [action.payload]);
        break;

      case types.CREATE_CUSTOMER_FAILURE:
        draft.modal.creation.loading = false;
        draft.modal.creation.visible = false;
        draft.apiError = action.payload;
        break;

      case types.UPDATE_CUSTOMER_SUCCESS:
        draft.isFetching = false;
        draft.data.all.items = { ...draft.data.all.items, [action.payload.id]: action.payload };
        draft.data.byId[action.payload.id] = action.payload;
        break;

      case types.UPDATE_CUSTOMER_FAILURE:
        draft.isFetching = false;
        draft.apiError = action.payload;
        break;

      case chat.FETCH_CONVERSATIONS_SUCCESS: {
        const customersCollection = action.payload.content.flatMap(extractCustomersFromConversation);
        addToByIds(draft, customersCollection);
        break;
      }

      case chat.POST_CONVERSATION_SUCCESS:
      case chat.FETCH_CONVERSATION_SUCCESS: {
        const customersCollection = extractCustomersFromConversation(action.payload);
        addToByIds(draft, customersCollection);
        break;
      }

      case chat.FETCH_MESSAGES_SUCCESS: {
        const usersCollection = action.payload.content.map(({ customer }) => customer).filter(customer => !!customer);

        draft.data.byId = {
          ...draft.data.byId,
          ...usersCollection.reduce(
            (newUsers, user) => ({
              ...newUsers,
              [user.id]: {
                ...(draft.data.byId[user.id] ?? {}),
                ...user,
              },
            }),
            {}
          ),
        };
        break;
      }
      default:
    }
    return draft;
  },
  initialState
);
export default reducer;
